/**
 * The landing's words (T13e-1), out of its components so its Markdown (/developers.md) reads the same ones: the
 * hero and what traveller-42 sees in your app, each section's head, the steps of the code that grows, what
 * each door says, the card's caption and the finale. Its code is the checked samples (content.ts).
 */
import type { IconName } from "../_family/Icon";
import type { Door } from "../_content/card";

export const HERO = {
  /** Two lines, broken on purpose: never three at any width. */
  title: ["Give every user an agent,", "right inside your app."],
  lede: "Your users ask in plain words. Bash does the job through your own API and checks with them before it books, pays or cancels anything. You write the functions, we run the agent.",
} as const;

/** The two ways on, under the hero and the finale. */
export const CTA = {
  testKey: "Get a test key",
  quickstart: "Build it in 15 minutes",
} as const;

/** Each section's head, in the page's order. */
export const SECTIONS = {
  path: {
    title: "Four steps from first ask to launch.",
    lede: "One traveller, start to finish. Each step adds a few lines to your code, and you watch what Bash does with them.",
  },
  doors: {
    title: "Call it from the stack you already have.",
    lede: "TypeScript, Python, the CLI, plain HTTP or MCP. Each one sends the same ask and gets back the same events.",
  },
  card: {
    title: "Eighteen commands cover the whole API.",
    lede: "Learn these and you can guess the rest. Every guide and reference page is organised around them.",
  },
  guides: {
    title: "Every job, step by step.",
    lede: "Approvals, your own API, tests, going live and more. Each guide is short, with code you can paste and what happens after each step.",
  },
} as const;

/** Under the code that grows, and the link that follows it. */
export const RAN = {
  says: "Guide 1 walks this path with your own key, and it ran end to end on our staging servers on 1 October.",
  link: "Open guide 1",
} as const;

/** The four steps: what each adds, in a teammate's colour, and what Bash does with it. */
export const STEPS = [
  {
    colour: "scout",
    title: "Ask for a user",
    pill: "Ask",
    says: "Name them by your own id. Their Bash is made the first time you do, and every step streams back.",
    file: "ts",
    next: "Next: your user says yes",
  },
  {
    colour: "ledger",
    title: "Your user says yes",
    pill: "Your user's yes",
    says: "Bash asks before anything that moves money, and takes the yes only from the person who saw it. Show the question in your app and pass the answer back.",
    file: "ts",
    next: "Next: Bash calls your API",
  },
  {
    colour: "hunter",
    title: "Bash calls your API",
    pill: "Your API",
    says: "Hand Bash a function. It calls it when the job needs your data, and the SDK answers with what it returns.",
    file: "ts",
    next: "Next: test, then ship",
  },
  {
    colour: "quill",
    title: "Test, then ship",
    pill: "Test, then ship",
    says: "Your tests live in vestra.yaml. A new version reaches your users only when they pass.",
    file: "yaml",
    next: "Start again",
  },
] as const;

/** What each door's window is called, its tab's icon, and one line about it (`code` in backticks). */
export const DOOR_SAYS: Record<
  Door,
  { file: string; icon: IconName; says: string }
> = {
  ts: {
    file: "ask.ts",
    icon: "fileTs",
    says: "Every step streams back as a typed event, and `run.text()` is the reply.",
  },
  py: {
    file: "ask.py",
    icon: "filePy",
    says: "The same names in Python. `AsyncVestra` has them too, each one awaited.",
  },
  cli: {
    file: "Terminal",
    icon: "terminal",
    says: "The reply streams into your terminal, and Bash asks for approvals right there.",
  },
  http: {
    file: "Terminal",
    icon: "globe",
    says: "`stream: true` sends this turn’s events as they happen, and ends when the turn settles.",
  },
  mcp: {
    file: "Any MCP client",
    icon: "mcp",
    says: "Point any MCP client at `/v1/mcp` with your key, or run `vestra mcp` inside your editor.",
  },
};

/** Under the card, until a command is pointed at. */
export const CARD_CAPTION =
  "Every command has its own page, with all five doors.";

/** The quickstart's row in the guides and references. */
export const QUICK_SAYS = "A test key, one ask, and your first yes.";

export const FINALE = {
  title: ["Get a test key.", "Build your first agent."],
  lede: "Nothing reaches a real user until you go live.",
} as const;

/** The hero's code window: its title in each door, and what each of the MCP door's calls is. */
export const HERO_CODE: {
  file: Record<Door, string>;
  mcp: readonly string[];
} = {
  file: {
    ts: "server.ts",
    py: "server.py",
    cli: "Terminal",
    http: "Terminal",
    mcp: "Any MCP client",
  },
  mcp: [
    "The ask, as a tool call. Your tools are your setup's; your server answers their calls.",
    "Your user's yes, once they saw the card.",
  ],
};

/**
 * What traveller-42 sees in your app: the card for book_flight's approval, as its interaction carries it (the
 * tool's description, then a row per input it is called with: ts/landing-hero), and Bash's reply after it.
 */
export const SEES = {
  title: "What traveller-42 sees in your app",
  asks: "Bash asks",
  waits: "waits for a yes",
  answered: "answered",
  says: "Books the new flight and charges its fare.",
  rows: [
    { label: "flight", value: "6E 2171, Delhi to Mumbai, 09:15 tomorrow" },
    { label: "fare", value: "Rs 5,420" },
    { label: "refund", value: "Rs 4,980 for the cancelled 6E 2134" },
  ],
  yes: "Yes, book it",
  booked: "Booked",
  no: "Not now",
  notBooked: "Not booked",
  reply: {
    yes: "You're on the 09:15 tomorrow, seat 14C. Your refund of Rs 4,980 is on its way.",
    no: "No problem. Nothing was booked, and nothing was charged.",
  },
} as const;
